import {
  Component,
  Inject,
  Input,
  OnInit,
  HostListener,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseService } from '../../services/base.service';
import { IError } from '../../interfaces';
import { ERROR_CODES, ERROR_MESSAGES, activeTranslations, LOGISTIC_TYPES } from '../../enum/constants';
import { NotificationService } from '../../services/notifications.service';
import { UkrPoshtaService } from '../../services/ukr-poshta.service';
import { NovaPoshtaService } from '../../services/nova-poshta.service';
import { TextFieldComponent } from '../../components/text-field/text-field.component';
import { PhoneFieldComponent } from '../../components/phone-field/phone-field.component';

interface IInvoiceForm {
  comments: FormControl;
  ttn: FormControl;
  phone: FormControl;
}

@Component({
  templateUrl: './create-track-invoice.component.html',
  styleUrls: ['./create-track-invoice.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CreateTrackInvoiceComponent implements OnInit {
  @Input() Error: boolean = false;

  @ViewChild('ttnField') ttnField!: TextFieldComponent;
  @ViewChild('phoneField') phoneField!: PhoneFieldComponent;
  @ViewChild('commentsField') commentsField!: ElementRef<HTMLTextAreaElement>;

  public isFormSubmitted: boolean = false;
  public isValidSubmitted: boolean = false;
  public form!: FormGroup<IInvoiceForm>;
  public list: any[] = [
    { value: 'string', name: 'Строка' },
    { value: 'number', name: 'Число' },
    { value: 'date', name: 'Дата та час' },
    { value: 'link', name: 'Посилання' },
    { value: 'lists', name: 'Список' },
    { value: 'checkbox', name: 'Чекбокс' },
  ];
  public isUpdate: boolean = false;
  private readonly type: LOGISTIC_TYPES;

  constructor(
    public dialogRef: MatDialogRef<CreateTrackInvoiceComponent>,
    private readonly ukrPoshtaService: UkrPoshtaService,
    private readonly novaPoshtaService: NovaPoshtaService,
    private readonly baseService: BaseService,
    private readonly notificationService: NotificationService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.isUpdate = data.data?.is_update;
    this.type = data.data?.type;
  }

  ngOnInit() {
    this.initForm();
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;

    // Control+Enter - Відправити форму
    if (event.ctrlKey && event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      if (this.isUpdate) {
        this.onUpdate();
      } else {
        this.onSubmit();
      }
      return;
    }

    // Enter - перемикання між полями (крім textarea)
    if (event.key === 'Enter' && target.tagName !== 'TEXTAREA') {
      event.preventDefault();
      this.focusNextField(target);
    }
  }

  private focusNextField(currentTarget: HTMLElement): void {
    // Перевіряємо, в якому полі зараз фокус
    const isTtnField = this.ttnField?.inputRef?.nativeElement === currentTarget;
    const isPhoneField = this.phoneField?.inputRef?.nativeElement === currentTarget;

    if (isTtnField) {
      // З TTN переходимо до телефону (якщо не update) або до коментарів
      if (!this.isUpdate && this.phoneField?.inputRef) {
        this.phoneField.inputRef.nativeElement.focus();
      } else if (this.commentsField?.nativeElement) {
        this.commentsField.nativeElement.focus();
      }
    } else if (isPhoneField) {
      // З телефону переходимо до коментарів
      if (this.commentsField?.nativeElement) {
        this.commentsField.nativeElement.focus();
      }
    }
  }

  protected isWindows(): boolean {
    return /Windows|Win32|Win64/.test(window.navigator.userAgent);
  }

  protected getModifierKey(): string {
    return this.isWindows() ? 'Ctrl' : 'Control';
  }

  initForm() {
    if (this.isUpdate) {
      this.form = new FormGroup<IInvoiceForm>({
        comments: new FormControl(this.data.data?.comments, Validators.maxLength(5000)),
        ttn: new FormControl(this.data.data?.ttn, [Validators.required, Validators.minLength(11)]),
        phone: new FormControl(this.baseService.currentUserInfo.phone),
      });
    } else {
      this.form = new FormGroup<IInvoiceForm>({
        comments: new FormControl('', Validators.maxLength(5000)),
        ttn: new FormControl('', [Validators.required, Validators.minLength(11)]),
        phone: new FormControl(this.baseService.currentUserInfo.phone, Validators.required),
      });
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  onSubmit() {
    this.isFormSubmitted = true;

    if (this.form.valid) {
      this.isValidSubmitted = true;

      if (this.type === LOGISTIC_TYPES.UKR_POSHTA) {
        this.ukrPoshtaService.createInvoice(this.form.getRawValue()).subscribe({
          next: () => {
            this.dialogRef.close(true);
          },
          error: (res) => {
            this.handleError(res);
          },
        });
      }

      if (this.type === LOGISTIC_TYPES.NOVA_POSHTA) {
        this.novaPoshtaService.createInvoice(this.form.getRawValue()).subscribe({
          next: () => {
            this.dialogRef.close(true);
          },
          error: (res) => {
            this.handleError(res);
          },
        });
      }
    }
  }

  onUpdate() {
    this.isFormSubmitted = true;

    // При update перевіряємо тільки необхідні поля (ttn та comments)
    const isTtnValid = this.form.controls.ttn.valid;
    const isCommentsValid = this.form.controls.comments.valid;

    if (isTtnValid && isCommentsValid) {
      this.isValidSubmitted = true;

      const formRawValue = this.form.getRawValue();
      const updatePayload = {
        ttn: formRawValue.ttn,
        comments: formRawValue.comments,
      };

      if (this.type === LOGISTIC_TYPES.NOVA_POSHTA) {
        this.novaPoshtaService.updateInvoice(this?.data?.data?.id, updatePayload).subscribe({
          next: () => {
            this.dialogRef.close(true);
          },
          error: (res) => {
            this.handleError(res);
          },
        });
      }

      if (this.type === LOGISTIC_TYPES.UKR_POSHTA) {
        this.ukrPoshtaService.updateInvoice(this?.data?.data?.id, updatePayload).subscribe({
          next: () => {
            this.dialogRef.close(true);
          },
          error: (res) => {
            this.handleError(res);
          },
        });
      }
    }
  }

  private handleError(err: any) {
    (err.error?.errors || []).forEach((err: IError) => {
      switch (err.code) {
        case ERROR_CODES.INTEGRATION_ALREADY_EXISTS: {
          this.notificationService.showError(
            '',
            activeTranslations.notifications_texts.error.attached_waybill,
          );
          this.isValidSubmitted = false;
          break;
        }

        case ERROR_CODES.TTN_NOT_FOUND: {
          this.notificationService.showError(
            '',
            activeTranslations.notifications_texts.error.ttn_not_found,
          );
          this.isValidSubmitted = false;
          break;
        }

        default: {
          this.notificationService.showError('', ERROR_MESSAGES[err.code]);
          this.isValidSubmitted = false;
          break;
        }
      }
    });
  }
}
